import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Dialog, DialogContent, DialogActions,
    Button, Box, Typography, CircularProgress, IconButton, Tooltip, useMediaQuery,
} from '@mui/material';
import { Download, Printer, ChevronLeft, ChevronRight } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import Cookies from 'js-cookie';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useToast } from '../../providers/toaster-provider';
import theme from '../../util/theme/theme';
import { SlideUpTransition } from './mobile/mobile-bottom-sheet';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface InvoicePreviewProps {
    open: boolean;
    invoiceId: string | number;
    onClose: () => void;
}

const InvoicePreview: React.FC<InvoicePreviewProps> = ({ open, invoiceId, onClose }) => {
    const { showError } = useToast();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const containerRef = useRef<HTMLDivElement>(null);
    const printFrameRef = useRef<HTMLIFrameElement>(null);

    const [numPages, setNumPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pdfWidth, setPdfWidth] = useState(600);
    const [pdfFile, setPdfFile] = useState<{ url: string; httpHeaders: Record<string, string> } | null>(null);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);

    // Fetch blob + build react-pdf file descriptor when dialog opens
    useEffect(() => {
        if (!open || !invoiceId) return;
        setCurrentPage(1);
        setNumPages(0);
        setBlobUrl(null);

        const { url } = buildApiRequestConfig(routes.invoices.downloadPdf, [{ param: 'id', value: invoiceId }]);
        const token = Cookies.get('token');
        const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

        setPdfFile({ url, httpHeaders: headers });

        // Fetch blob for print/download (auth headers included)
        fetch(url, { headers })
            .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
            .then(blob => setBlobUrl(URL.createObjectURL(blob)))
            .catch(() => {});
    }, [open, invoiceId]);

    // Revoke blob URL on close
    useEffect(() => {
        if (!open) {
            setPdfFile(null);
            if (blobUrl) { URL.revokeObjectURL(blobUrl); setBlobUrl(null); }
        }
    }, [open]);

    // Responsive width
    useEffect(() => {
        if (!open) return;
        const update = () => {
            if (containerRef.current) {
                setPdfWidth(Math.min(containerRef.current.clientWidth - 32, 800));
            }
        };
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [open]);

    const handlePrint = useCallback(() => {
        printFrameRef.current?.contentWindow?.print();
    }, []);

    const handleDownload = useCallback(() => {
        if (!blobUrl) return;
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `Invoice-${invoiceId}.pdf`;
        a.click();
    }, [blobUrl, invoiceId]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={isMobile ? false : "md"}
            fullWidth
            {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
            sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
            PaperProps={{
                sx: isMobile
                    ? { m: 0, width: '100%', maxWidth: '100%', height: '90vh', maxHeight: '90vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, display: 'flex', flexDirection: 'column' }
                    : { height: '90vh', borderRadius: 3, display: 'flex', flexDirection: 'column' }
            }}
        >
            <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
                {/* Page navigation bar */}
                {numPages > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1, borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
                        <Tooltip title="Previous page">
                            <span>
                                <IconButton size="small" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}>
                                    <ChevronLeft size={18} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', minWidth: 80, textAlign: 'center' }}>
                            Page {currentPage} of {numPages}
                        </Typography>
                        <Tooltip title="Next page">
                            <span>
                                <IconButton size="small" onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))} disabled={currentPage >= numPages}>
                                    <ChevronRight size={18} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                )}

                {/* Hidden iframe for printing — loads the blob URL so no auth needed */}
                {blobUrl && (
                    <iframe ref={printFrameRef} src={blobUrl} style={{ display: 'none' }} title="print-frame" />
                )}

                {/* PDF area */}
                {/* overflowY: 'scroll' (not 'auto') - the scrollbar's track
                    space is reserved unconditionally, whether or not this
                    page's content actually overflows. With 'auto', the
                    scrollbar only reserving space while shown creates a
                    feedback loop with the width measurement below: at a
                    borderline page height, the PDF's rendered width (and
                    therefore height, since react-pdf keeps the aspect ratio)
                    is computed from clientWidth, which itself depends on
                    whether the scrollbar is currently showing - so showing
                    it can make the content fit, hiding it again, which
                    un-fits it again, and so on. Forcing the track always
                    present removes that dependency entirely. */}
                <Box ref={containerRef} sx={{ flex: 1, overflowY: 'scroll', display: 'flex', justifyContent: 'center', p: 2, background: 'var(--color-container-background)' }}>
                    {pdfFile && (
                        <Document
                            file={pdfFile}
                            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                            onLoadError={() => showError('Failed to load invoice PDF')}
                            loading={
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pt: 8, gap: 2 }}>
                                    <CircularProgress size={36} sx={{ color: 'var(--color-primary)' }} />
                                    <Typography sx={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Loading invoice…</Typography>
                                </Box>
                            }
                        >
                            <Page
                                pageNumber={currentPage}
                                width={pdfWidth}
                                renderTextLayer
                                renderAnnotationLayer
                                loading={null}
                            />
                        </Document>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1.5, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #f0f0f0' }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, '&:hover': { background: 'var(--color-container-background)' } }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InvoicePreview;
